import { type TablePreferencesConfig } from 'src/common/ui/TablePreferences';

import { MAX_STRINGS_PER_GROUP } from '../constants';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** Config for the strings table; ids and order mirror `StringsTable`. */

/**
 * Actions holds the row's only way into a string, so it is `alwaysVisible` and
 * pinned last. No CSS depends on its position, unlike the search table's.
 */
const ACTIONS_COLUMN_ID = 'actions';

/** 7, not a round number: what the table has always shown. */
export const STRINGS_DEFAULT_PAGE_SIZE = 7;

/** Capped at `MAX_STRINGS_PER_GROUP`: a larger size could never page. */
export const STRINGS_PAGE_SIZE_OPTIONS: readonly number[] = [
  STRINGS_DEFAULT_PAGE_SIZE,
  15,
  MAX_STRINGS_PER_GROUP,
];

/**
 * Builds the strings-table preferences config.
 *
 * The per-string Source Locale column is offered only for groups owned by a
 * non-LangHub client (`includeSourceLocale`). DescribeStringGroup returns no
 * group-level source locale for those groups by design — each string may carry
 * a different one — so the locale is surfaced per string here instead of once
 * in the metadata panel. LangHub-owned groups share a single group-level source
 * locale (shown in the metadata panel), so repeating it on every row would be
 * redundant.
 */
export function buildStringsTablePreferences(
  t: TranslateFn,
  {
    includeSourceLocale,
  }: {
    includeSourceLocale: boolean;
  },
): TablePreferencesConfig {
  return {
    storageKey: 'LangHub-stringsTablePreferences',
    columnOptions: [
      // alwaysVisible: this cell holds the variant expand toggle.
      {
        id: 'stringId',
        label: t('string-field-string-id'),
        alwaysVisible: true,
      },
      ...(includeSourceLocale
        ? [{ id: 'sourceLocale', label: t('sgd-field-source-locale') }]
        : []),
      { id: 'sourceText', label: t('sgd-column-source-text') },
      { id: 'contextNote', label: t('sgd-column-context-note') },
      {
        id: ACTIONS_COLUMN_ID,
        label: t('string-column-actions'),
        alwaysVisible: true,
      },
    ],
    pageSizeOptions: STRINGS_PAGE_SIZE_OPTIONS,
    defaultPageSize: STRINGS_DEFAULT_PAGE_SIZE,
    pinnedLastColumnId: ACTIONS_COLUMN_ID,
  };
}
